import { Injectable } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { catchError, Observable, Subject, switchMap, throwError } from 'rxjs';
import { AuthService } from '@core/services/auth/auth.service';
import { filter, finalize, take } from 'rxjs/operators';
import { AUTH_REQUIRED } from '@core/tokens/auth-required.token';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private tokenRefreshing = false;
  private refreshTokenSubject$: Subject<string | null> = new Subject<
    string | null
  >();

  constructor(private authService: AuthService) {}

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    if (!request.context.get(AUTH_REQUIRED)) {
      return next.handle(request);
    }
    const token = this.authService.getAuthData().access_token;
    if (token) {
      request = this.addTokenToRequest(request, token);
    }

    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error && error.status === 401) {
          return this.handle401Error(request, next);
        } else {
          return throwError(() => error);
        }
      })
    );
  }

  private handle401Error(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    if (!this.tokenRefreshing) {
      this.tokenRefreshing = true;
      this.refreshTokenSubject$.next(null);

      return this.authService.refreshAuthToken().pipe(
        switchMap((response) => {
          const newToken = response.auth_data.access_token;
          this.refreshTokenSubject$.next(newToken);
          this.tokenRefreshing = false;
          return next.handle(this.addTokenToRequest(request, newToken));
        }),
        catchError((error) => {
          this.tokenRefreshing = false;
          this.authService.signOut();
          return throwError(() => error);
        }),
        finalize(() => {
          this.tokenRefreshing = false;
        })
      );
    } else {
      return this.refreshTokenSubject$.pipe(
        filter((token) => token != null),
        take(1),
        switchMap((token) => {
          if (token) {
            return next.handle(this.addTokenToRequest(request, token));
          } else {
            return throwError(
              () =>
                new HttpErrorResponse({
                  status: 401,
                  statusText: 'Unauthorized',
                })
            );
          }
        })
      );
    }
  }

  private addTokenToRequest(
    request: HttpRequest<unknown>,
    token: string
  ): HttpRequest<unknown> {
    return request.clone({
      setHeaders: { Authorization: `Bearer ${token}` },
      withCredentials: true,
    });
  }
}
